import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiArrowRight, FiCamera, FiCheck, FiWifiOff } from 'react-icons/fi';
import './AndroidOnboarding.css';

const COPY = {
  fr: {
    skip: 'Passer', next: 'Suivant', start: 'Commencer', back: 'Précédent', language: 'Langue', step: 'Étape',
    steps: [
      { title: 'Des idées pour vos terres.', description: 'Un assistant pour explorer l’agroécologie et trouver des pistes adaptées à vos cultures.', detail: 'Des réponses pour avancer, une question à la fois.' },
      { title: 'Une question, une photo.', description: 'Décrivez votre situation ou ajoutez une photo pour enrichir vos échanges avec UpyaChat.', detail: 'L’IA a besoin d’Internet pour vous répondre.' },
      { title: 'Vos échanges, même au champ.', description: 'Retrouvez les conversations déjà enregistrées sur ce téléphone, même sans réseau.', detail: 'Chargez vos échanges avant de partir. Les liens et pièces jointes peuvent nécessiter Internet.' },
    ],
    question: 'Comment améliorer la vie de mon sol ?', example: 'Parlons de votre sol et de vos pratiques.', saved: 'Disponible hors connexion', field: 'Mon sol, mes cultures', offline: 'À relire au champ',
  },
  en: {
    skip: 'Skip', next: 'Next', start: 'Get started', back: 'Back', language: 'Language', step: 'Step',
    steps: [
      { title: 'Grow your ideas. Care for your land.', description: 'An assistant to explore agroecology and discover approaches suited to your crops.', detail: 'Find a way forward, one question at a time.' },
      { title: 'A question. A photo. A conversation.', description: 'Describe your situation or add a photo to help UpyaChat understand your question.', detail: 'The AI needs an Internet connection to answer.' },
      { title: 'Take your conversations into the field.', description: 'Read conversations already saved on this phone, even without a connection.', detail: 'Load your conversations before heading out. Links and attachments may need Internet.' },
    ],
    question: 'How can I improve life in my soil?', example: 'Let’s talk about your soil and practices.', saved: 'Available offline', field: 'My soil, my crops', offline: 'Ready to read in the field',
  },
};

function FieldDrawing() {
  return <svg className="uo-field-drawing" viewBox="0 0 320 240" fill="none" aria-hidden="true">
    <circle cx="160" cy="110" r="95" fill="#e8f1ef" />
    <circle cx="238" cy="52" r="17" fill="#d8e5b9" />
    <path d="M30 202c62-50 123-50 180 0M101 225c66-65 131-78 193-35M29 222c64-49 108-43 146-12" stroke="#a7c2b6" strokeWidth="2" strokeLinecap="round" />
    <path d="M159 186V92" stroke="#2a5d55" strokeWidth="4" strokeLinecap="round" />
    <path d="M158 133c-30 1-47-16-43-42 29-1 46 16 43 42Z" fill="#7dada4" />
    <path d="M161 116c-2-30 16-48 47-47 1 28-16 47-47 47Z" fill="#2a5d55" />
    <path d="M160 90c-16-18-14-39 2-57 17 20 16 40-2 57Z" fill="#afc8a4" />
    <path d="m159 153-32-30m34 11 34-35" stroke="#2a5d55" strokeWidth="2" strokeLinecap="round" />
    <path d="M84 186v-33m0 17c-14 0-23-8-21-21 14 0 22 9 21 21Zm1-11c-1-12 7-21 21-21 1 13-8 21-21 21Z" stroke="#7dada4" strokeWidth="2" strokeLinejoin="round" />
  </svg>;
}

function Illustration({ step, text }) {
  if (step === 0) return <div className="uo-illustration"><FieldDrawing /></div>;
  if (step === 1) return <div className="uo-illustration uo-example" aria-hidden="true">
    <div className="uo-question"><FiCamera /><span>{text.question}</span></div>
    <div className="uo-answer"><img src="/Logo.png" alt="" /><div><strong>UpyaChat</strong><span>{text.example}</span><i /><i /></div></div>
    <svg viewBox="0 0 100 70" className="uo-small-leaf" fill="none"><path d="M22 58C15 23 48 6 79 12 84 43 58 64 22 58Z" fill="#dce8de" /><path d="m16 65 50-40" stroke="#7dada4" strokeWidth="3" strokeLinecap="round" /></svg>
  </div>;
  return <div className="uo-illustration uo-offline-scene" aria-hidden="true">
    <div className="uo-offline-orbit"><FiWifiOff /></div>
    <div className="uo-saved-conversation"><span className="uo-saved-status"><FiCheck />{text.saved}</span><strong>{text.field}</strong><span className="uo-reading-line" /><span className="uo-reading-line" /><span className="uo-reading-line" /><div className="uo-saved-bottom"><img src="/Logo.png" alt="" /><span>{text.offline}</span></div></div>
  </div>;
}

export default function AndroidOnboarding({ onComplete }) {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const text = COPY[language];
  const [step, setStep] = useState(0);
  const title = useRef(null);
  const moveTo = (nextStep) => {
    setStep(nextStep);
    // Keep focus in the content after changing steps for keyboard/screen-reader users.
    title.current?.focus();
  };
  return <main className="upya-onboarding" lang={language} dir="ltr">
    <div className="uo-shell">
      <header className="uo-header"><div className="uo-brand"><img src="/Logo.png" alt="" /><span>UpyaChat</span></div>
      <div className="uo-language" role="group" aria-label={text.language}>
        <button type="button" lang="fr" aria-label="Français" aria-pressed={language === 'fr'} onClick={() => i18n.changeLanguage('fr')}>FR</button>
        <button type="button" lang="en" aria-label="English" aria-pressed={language === 'en'} onClick={() => i18n.changeLanguage('en')}>EN</button>
      </div>
      <button type="button" className="uo-skip" onClick={onComplete}>{text.skip}</button></header>
      <section className="uo-content" aria-labelledby="uo-title">
        <Illustration step={step} text={text} />
        <div className="uo-copy" aria-live="polite" aria-atomic="true"><h1 id="uo-title" ref={title} tabIndex={-1}>{text.steps[step].title}</h1><p>{text.steps[step].description}</p><p className="uo-detail">{text.steps[step].detail}</p></div>
      </section>
      <footer className="uo-footer">
        <nav className="uo-progress" aria-label={language === 'fr' ? 'Étapes de présentation' : 'Introduction steps'}>{text.steps.map((item, index) => <button type="button" key={index} aria-label={`${text.step} ${index + 1} / 3 : ${item.title}`} aria-current={step === index ? 'step' : undefined} onClick={() => moveTo(index)}><span /></button>)}</nav>
        <div className="uo-actions">{step > 0 && <button type="button" className="uo-back" aria-label={text.back} onClick={() => moveTo(step - 1)}><FiArrowLeft /></button>}<button type="button" className="uo-next" onClick={() => step === 2 ? onComplete() : moveTo(step + 1)}>{step === 2 ? text.start : text.next}<FiArrowRight aria-hidden="true" /></button></div>
      </footer>
    </div>
  </main>;
}
